import { API_ORIGIN } from '@/constants'
import { managedEmployerOrgSlugStorageKey } from '@/constants'
import { authorizedFetch } from '@/lib/api'
import { fetchManagedOrganisationSlugs } from '@/lib/employer-posting-preferences-api'
import type { Organisation } from '@/hooks/use-organisations'

export async function resolveEmployerOnboardingOrgSlug(
  preferredSlug?: string | null,
): Promise<string | null> {
  const trimmed = preferredSlug?.trim()
  if (trimmed) return trimmed

  try {
    const stored = window.localStorage.getItem(managedEmployerOrgSlugStorageKey())?.trim()
    if (stored) return stored
  } catch {
    /* ignore */
  }

  try {
    const slugs = await fetchManagedOrganisationSlugs()
    return slugs[0] ?? null
  } catch {
    return null
  }
}

export async function createManagedOrganisation(
  name: string,
  extra: { website?: string } = {},
): Promise<{ slug: string }> {
  const response = await authorizedFetch(`${API_ORIGIN}/api/organisations/managed`, {
    method: 'POST',
    body: JSON.stringify({ name: name.trim(), ...extra }),
  })

  if (!response.ok) {
    let message = 'Failed to create organisation'
    try {
      const error = await response.json()
      message =
        (typeof error?.error === 'string' && error.error) ||
        (typeof error?.detail === 'string' && error.detail) ||
        message
    } catch {
      /* ignore */
    }
    throw new Error(message)
  }

  const org = (await response.json()) as { slug?: string }
  const slug = org.slug?.trim()
  if (!slug) {
    throw new Error('Organisation created but slug missing from response')
  }

  try {
    window.localStorage.setItem(managedEmployerOrgSlugStorageKey(), slug)
  } catch {
    /* ignore */
  }

  return { slug }
}

export type ManagedOrganisationOnboardingProfile = Pick<
  Organisation,
  | 'name'
  | 'description'
  | 'website'
  | 'industry'
  | 'company_size'
  | 'founded_year'
  | 'city'
  | 'country'
  | 'linkedin_url'
  | 'x_url'
  | 'instagram_url'
  | 'facebook_url'
  | 'youtube_url'
  | 'github_url'
  | 'tiktok_url'
>

export async function updateManagedOrganisationProfile(
  slug: string,
  data: Partial<ManagedOrganisationOnboardingProfile>,
): Promise<void> {
  const response = await authorizedFetch(
    `${API_ORIGIN}/api/organisations/managed/${encodeURIComponent(slug)}/`,
    {
      method: 'PUT',
      body: JSON.stringify(data),
    },
  )

  if (!response.ok) {
    let message = 'Failed to save company information'
    try {
      const error = await response.json()
      message =
        (typeof error?.error === 'string' && error.error) ||
        (typeof error?.detail === 'string' && error.detail) ||
        message
    } catch {
      /* ignore */
    }
    throw new Error(message)
  }
}

export async function loadManagedOrganisationProfile(
  slug: string,
): Promise<ManagedOrganisationOnboardingProfile> {
  const response = await authorizedFetch(
    `${API_ORIGIN}/api/organisations/managed/${encodeURIComponent(slug)}/`,
  )
  if (!response.ok) {
    throw new Error('Failed to load organisation')
  }
  const org = (await response.json()) as Partial<Organisation>
  return {
    name: org.name ?? '',
    description: org.description ?? '',
    website: org.website ?? '',
    industry: org.industry ?? '',
    company_size: org.company_size ?? '',
    founded_year: org.founded_year ?? null,
    city: org.city ?? '',
    country: org.country ?? '',
    linkedin_url: org.linkedin_url ?? '',
    x_url: org.x_url ?? '',
    instagram_url: org.instagram_url ?? '',
    facebook_url: org.facebook_url ?? '',
    youtube_url: org.youtube_url ?? '',
    github_url: org.github_url ?? '',
    tiktok_url: org.tiktok_url ?? '',
  }
}
